import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useApolloClient, useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useMe } from "@/auth/use-me";
import { Button } from "@/components/design/Button";
import { ExtractionDialog } from "@/components/save/ExtractionDialog";
import {
  MyDocumentGroupsDocument,
  MyDocumentsDocument,
  PowerAnalysisDocumentsDocument,
} from "@/graphql/generated/operations";
import { toExtractionDoc } from "@/pages/save/service/use-my-documents";

const PAGE_FETCH = 100;

export function SmartAnalysisButton({ id, label }: { id?: string; label?: ReactNode }) {
  const intl = useIntl();
  const client = useApolloClient();
  const me = useMe();
  const [open, setOpen] = useState(false);
  const [documentsFailed, setDocumentsFailed] = useState(false);
  const { data, loading, error, fetchMore } = useQuery(MyDocumentsDocument, {
    variables: { input: { first: PAGE_FETCH } },
    skip: !open,
    fetchPolicy: "cache-and-network",
  });
  const { data: groupsData } = useQuery(MyDocumentGroupsDocument, {
    variables: { input: {} },
    skip: !open,
  });
  const pageInfo = data?.documents?.pageInfo;

  useEffect(() => {
    if (!open || documentsFailed || !pageInfo?.hasNextPage || !pageInfo.endCursor) return;
    void fetchMore({
      variables: { input: { first: PAGE_FETCH, after: pageInfo.endCursor } },
    }).catch(() => setDocumentsFailed(true));
  }, [open, documentsFailed, pageInfo, fetchMore]);

  const docs = useMemo(() => (data?.documents?.nodes ?? []).map(toExtractionDoc), [data]);
  const groups = groupsData?.documentGroups?.documentGroups ?? [];

  return (
    <>
      <Button
        id={id}
        variant="secondary"
        size="compact"
        onClick={() => {
          setDocumentsFailed(false);
          setOpen(true);
        }}
      >
        {label ?? <FormattedMessage id="grow.assess.analyseDocuments" defaultMessage="Analyse documents" />}
      </Button>
      {open ? (
        <ExtractionDialog
          title={intl.formatMessage({
            id: "service.page.powerAnalysisTitle",
            defaultMessage: "Power analysis",
          })}
          flags={{ extractPowers: true }}
          docs={docs}
          groups={groups}
          docsLoading={!error && !documentsFailed && (loading || !data || !!pageInfo?.hasNextPage)}
          docsLoadError={Boolean(error) || documentsFailed}
          personId={me.id ?? null}
          onClose={() => setOpen(false)}
          onDone={() => void client.refetchQueries({
            include: [MyDocumentsDocument, PowerAnalysisDocumentsDocument],
          })}
        />
      ) : null}
    </>
  );
}
